import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Clipboard,
  Linking,
  Alert,
  Modal,
  TextInput,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { observer, use$ } from '@legendapp/state/react';
import { Ionicons } from '@expo/vector-icons';
import { appState$, addCoins, setLanguage, setUserName } from '../utils/AppState';
import { currentUser$ } from '../utils/Firebase';

interface SettingsItem {
  id: string;
  title: string;
  subtitle?: string;
  icon: keyof typeof Ionicons.glyphMap;
}


const SettingsScreen = observer(() => {
  const insets = useSafeAreaInsets();
  const coins = use$(appState$.coins);
  const streak = use$(appState$.streak);
  const streakBest = use$(appState$.streakBest);
  const language = use$(appState$.language);
  const user = use$(currentUser$);
  const userName = use$(appState$.userName);
  const [langModalVisible, setLangModalVisible] = useState(false);
  const [nameModalVisible, setNameModalVisible] = useState(false);
  const [newName, setNewName] = useState('');

  const handleCopyUserId = () => {
    if (user?.uid) {
      Clipboard.setString(user.uid);
      Alert.alert('Copied', 'User ID copied to clipboard.');
    } else {
      Alert.alert('Error', 'No user ID available.');
    }
  };

  const handlePurchaseCoins = () => {
    Alert.alert(
      'Purchase Coins',
      'Choose a coin pack:',
      [
        { text: '100 Coins ($0.99)', onPress: () => addCoins(100) },
        { text: '500 Coins ($3.99)', onPress: () => addCoins(500) },
        { text: '2000 Coins ($9.99)', onPress: () => addCoins(2000) },
        { text: 'Cancel', style: 'cancel' },
      ]
    );
  };

  const handleJourney = () => {
    Alert.alert(
      'Love Journey',
      `Current streak: ${streak} days\nBest streak: ${streakBest} days\n\nCome back every day to keep your streak alive!`
    );
  };

  const handleLanguage = () => {
    setLangModalVisible(true);
  };

  const handleEmail = () => {
    Linking.openURL('mailto:support@virtualhalf.com?subject=Support Request').catch(() =>
      Alert.alert('Error', 'Could not open email client.')
    );
  };

  const handleRate = () => {
    Linking.openURL('https://play.google.com/store/apps/details?id=com.virtualhalf.app').catch(() =>
      Alert.alert('Error', 'Could not open Play Store.')
    );
  };

  const handleOtherApps = () => {
    Alert.alert('Our Other Apps', 'Check out more apps from us soon!');
  };

  const handleHelp = () => {
    Alert.alert(
      'Help Center',
      'Frequently Asked Questions:\n\n1. How do I earn coins?\nPlay mini-games and maintain your daily streak.\n\n2. How do I unlock characters?\nTap on a locked character and spend 50 coins.\n\n3. What is Spicy Chat?\nEnable it in chat for more playful responses.\n\n4. Is my data safe?\nYes, all chats are encrypted and stored securely.'
    );
  };

  const handleChangeUsername = () => {
    setNewName(appState$.userName.get() ?? '');
    setNameModalVisible(true);
  };

  const saveUsername = () => {
    const trimmed = newName.trim();
    if (trimmed.length < 2) {
      Alert.alert('Invalid Name', 'Username must be at least 2 characters.');
      return;
    }
    setUserName(trimmed);
    setNameModalVisible(false);
  };

  const actions: Record<string, () => void> = {
    'user-id': handleCopyUserId,
    username: handleChangeUsername,
    coins: handlePurchaseCoins,
    journey: handleJourney,
    language: handleLanguage,
    email: handleEmail,
    rate: handleRate,
    apps: handleOtherApps,
    help: handleHelp,
  };

  const items: SettingsItem[] = [
    { id: 'user-id', title: 'Copy User ID', icon: 'person' },
    { id: 'username', title: 'Change Username', subtitle: userName ?? 'Guest', icon: 'person-circle' },
    { id: 'coins', title: 'Purchase Coins', icon: 'cart' },
    {
      id: 'journey',
      title: 'Love Journey',
      subtitle: `🔥 ${streak} day streak · Best: ${streakBest}`,
      icon: 'flame',
    },
    { id: 'language', title: 'Change Language', subtitle: language.toUpperCase(), icon: 'language' },
    { id: 'email', title: 'Contact On Email', icon: 'mail' },
    { id: 'rate', title: 'Rate on Play Store', icon: 'star' },
    { id: 'apps', title: 'Our Other Apps', icon: 'grid' },
    { id: 'help', title: 'Help Center', icon: 'help-circle' },
  ];

  return (
    <View style={styles.container}>
      <View style={{ height: insets.top }} />

      <View style={styles.headerRow}>
        <Text style={styles.title}>General Settings</Text>
        <View style={styles.coinBadge}>
          <Ionicons name="logo-bitcoin" size={16} color="#FFCA38" />
          <Text style={styles.coinText}>{coins}</Text>
        </View>
      </View>

      <ScrollView
        contentContainerStyle={styles.listContent}
        showsVerticalScrollIndicator={false}
      >
        {items.map((item) => (
          <TouchableOpacity
            key={item.id}
            style={styles.item}
            activeOpacity={0.85}
            onPress={actions[item.id]}
          >
            <View style={styles.itemIconWrap}>
              <Ionicons name={item.icon} size={18} color="#D347FF" />
            </View>

            <View style={styles.itemTextWrap}>
              <Text style={styles.itemTitle}>{item.title}</Text>
              {item.subtitle ? (
                <Text style={styles.itemSubtitle}>{item.subtitle}</Text>
              ) : null}
            </View>

            <Ionicons name="chevron-forward" size={20} color="#6E728F" />
          </TouchableOpacity>
        ))}
      </ScrollView>

      <Modal
        animationType="slide"
        transparent
        visible={nameModalVisible}
        onRequestClose={() => setNameModalVisible(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Change Username</Text>
            <TextInput
              style={styles.nameInput}
              value={newName}
              onChangeText={setNewName}
              placeholder="Enter new username"
              placeholderTextColor="#6E728F"
              autoCapitalize="none"
              autoCorrect={false}
              maxLength={20}
            />
            <TouchableOpacity style={styles.saveButton} activeOpacity={0.8} onPress={saveUsername}>
              <Text style={styles.saveButtonText}>Save</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.modalClose}
              onPress={() => setNameModalVisible(false)}
            >
              <Text style={styles.modalCloseText}>Cancel</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      <Modal
        animationType="slide"
        transparent
        visible={langModalVisible}
        onRequestClose={() => setLangModalVisible(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Select Language</Text>
            {[
              { code: 'en', label: 'English' },
              { code: 'hi', label: 'Hindi' },
              { code: 'es', label: 'Spanish' },
              { code: 'fr', label: 'French' },
            ].map((lang) => (
              <TouchableOpacity
                key={lang.code}
                style={[
                  styles.langOption,
                  language === lang.code && styles.langOptionActive,
                ]}
                onPress={() => {
                  setLanguage(lang.code);
                  setLangModalVisible(false);
                }}
              >
                <Text
                  style={[
                    styles.langLabel,
                    language === lang.code && styles.langLabelActive,
                  ]}
                >
                  {lang.label}
                </Text>
                {language === lang.code && (
                  <Ionicons name="checkmark" size={18} color="#DB47FF" />
                )}
              </TouchableOpacity>
            ))}
            <TouchableOpacity
              style={styles.modalClose}
              onPress={() => setLangModalVisible(false)}
            >
              <Text style={styles.modalCloseText}>Close</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#080B1F',
    paddingHorizontal: 16,
  },
  headerRow: {
    marginTop: 12,
    marginBottom: 22,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  title: {
    color: '#F5F7FF',
    fontSize: 35,
    fontWeight: '700',
    maxWidth: '70%',
  },
  coinBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: '#2A2D42',
    borderRadius: 10,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.12)',
  },
  coinText: {
    color: '#F5F7FF',
    fontSize: 17,
    fontWeight: '700',
  },
  listContent: {
    paddingBottom: 20,
    gap: 12,
  },
  item: {
    backgroundColor: '#191D33',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    borderRadius: 18,
    minHeight: 72,
    paddingHorizontal: 16,
    flexDirection: 'row',
    alignItems: 'center',
  },
  itemIconWrap: {
    width: 38,
    height: 38,
    borderRadius: 19,
    backgroundColor: '#3A274D',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 14,
  },
  itemTextWrap: {
    flex: 1,
  },
  itemTitle: {
    color: '#FFFFFF',
    fontSize: 17,
    fontWeight: '600',
  },
  itemSubtitle: {
    marginTop: 3,
    color: '#A6ABCA',
    fontSize: 13,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.6)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    backgroundColor: '#191D33',
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    padding: 24,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
  },
  modalTitle: {
    color: '#F5F7FF',
    fontSize: 20,
    fontWeight: '700',
    marginBottom: 16,
  },
  langOption: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 14,
    paddingHorizontal: 16,
    borderRadius: 12,
    marginBottom: 8,
    backgroundColor: '#12152B',
  },
  langOptionActive: {
    backgroundColor: 'rgba(219,71,255,0.15)',
    borderWidth: 1,
    borderColor: 'rgba(219,71,255,0.4)',
  },
  langLabel: {
    color: '#C7CBE8',
    fontSize: 16,
    fontWeight: '500',
  },
  langLabelActive: {
    color: '#DB47FF',
    fontWeight: '700',
  },
  modalClose: {
    marginTop: 12,
    alignSelf: 'center',
    paddingVertical: 10,
    paddingHorizontal: 32,
    borderRadius: 20,
    backgroundColor: '#3A274D',
  },
  modalCloseText: {
    color: '#F5F7FF',
    fontSize: 15,
    fontWeight: '600',
  },
  nameInput: {
    backgroundColor: '#12152B',
    borderRadius: 14,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.15)',
    paddingHorizontal: 16,
    paddingVertical: 14,
    color: '#F5F7FF',
    fontSize: 16,
    marginBottom: 16,
  },
  saveButton: {
    backgroundColor: '#B748FF',
    borderRadius: 14,
    paddingVertical: 14,
    alignItems: 'center',
    marginBottom: 8,
  },
  saveButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '700',
  },
});

export default SettingsScreen;
